import { ButtonGroupRow, ButtonPopup, LoadingIndicator, TextHeading } from '@isdd/idsk-ui-kit'
import { ButtonLink } from '@isdd/idsk-ui-kit/button-link/ButtonLink'
import { Tooltip } from '@isdd/idsk-ui-kit/tooltip/Tooltip'
import { Del } from '@isdd/idsk-ui-kit/typography/Del'
import {
    ApiError,
    ConfigurationItemUi,
    ConfigurationItemUiAttributes,
    getReadNeighboursConfigurationItemsQueryKey,
    useGetRequestStatusHook,
    useGetRoleParticipant,
    useGetUuidHook,
    useReadCiHistoryVersionsHook,
    useReadCiList1Hook,
    useReadNeighboursConfigurationItems,
} from '@isdd/metais-common/api/generated/cmdb-swagger'
import {
    getIsOwnerByGidQueryKey,
    Role,
    useAddOrGetGroupHook,
    useFindAll11Hook,
    useIsInPoByGidHook,
    useIsOwnerByGid,
} from '@isdd/metais-common/api/generated/iam-swagger'
import {
    getGetKrisQueryKey,
    GraphRequestUi,
    useGetKris,
    useRecycleConfigurationItemBiznisMdulesHook,
    useStoreRequestHook,
} from '@isdd/metais-common/api/generated/kris-swagger'
import { useGetKrisFuturePdfHook } from '@isdd/metais-common/api/generated/pdf-creator'
import { getGetDocumentQueryKey, useGetDocument, useGetDocumentHook } from '@isdd/metais-common/api/generated/wiki-swagger'
import { IApproveFormData, useUpdateKirtColumnsHook } from '@isdd/metais-common/api/userConfigKvKrit'
import { downloadBlobAsFile } from '@isdd/metais-common/componentHelpers/download/downloadHelper'
import styles from '@isdd/metais-common/components/entity-header/ciEntityHeader.module.scss'
import { useAuth } from '@isdd/metais-common/contexts/auth/authContext'
import { Can } from '@isdd/metais-common/hooks/permissions/useAbilityContext'
import { Actions } from '@isdd/metais-common/hooks/permissions/useUserAbility'
import { IBulkActionResult, useBulkAction } from '@isdd/metais-common/hooks/useBulkAction'
import { useScroll } from '@isdd/metais-common/hooks/useScroll'
import { ATTRIBUTE_NAME, InvalidateBulkModal, KRIT_HODNOTENIE, MutationFeedback } from '@isdd/metais-common/index'
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ENTITY_KRIS } from '@isdd/metais-common/constants'

import { ReValidateModal } from './Modals/ReValidateModal'

import {
    FILTER_PARAM,
    createNewKrisAttributes,
    getBaseGraphApprove,
    getBaseGraphReturnToWorkout,
    getBaseGraphSendToApproving,
    getOriginalOwner,
} from '@/components/views/ci/kris/KrisEntityHelper'
import { ApproveModal } from '@/components/views/ci/kris/Modals/ApproveModal'
import { GeneratePdfModal, IGeneratePdfFormData } from '@/components/views/ci/kris/Modals/GeneratePdfModal'
import { IReturnToWorkoutFormData, ReturnToWorkoutModal } from '@/components/views/ci/kris/Modals/ReturnToWorkoutModal'
import { useOutletContext } from '@/pages/ci/KRIS/[entityId]'

interface Props {
    entityName: string
    entityId: string
    entityItemName: string
    entityData?: ConfigurationItemUi
    isInvalidated: boolean
    refetchCi: <TPageData extends unknown[]>(
        options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
    ) => Promise<QueryObserverResult<ConfigurationItemUi, ApiError>>
    isRelation?: boolean
    isEvaluation: boolean
    editButton: React.ReactNode
}

export const KrisEntityIdHeader: React.FC<Props> = ({
    entityData,
    entityId,
    entityItemName,
    isInvalidated,
    refetchCi,
    isRelation,
    editButton,
    isEvaluation,
}) => {
    const { t } = useTranslation()
    const {
        state: { user, token },
    } = useAuth()
    const { updateButton } = useOutletContext()
    const queryClient = useQueryClient()

    const { handleInvalidate, errorMessage, isBulkLoading } = useBulkAction(isRelation)
    const updateProtokol = useUpdateKirtColumnsHook()
    const approveKris = useStoreRequestHook()
    const getCiListFIltered = useReadCiList1Hook()
    const getFindRole = useFindAll11Hook()
    const createGroup = useAddOrGetGroupHook()
    const getHistoryVersions = useReadCiHistoryVersionsHook()
    const generateUuid = useGetUuidHook()
    const getRequestStatus = useGetRequestStatusHook()
    const isInPoByGid = useIsInPoByGidHook()
    const createPdf = useGetKrisFuturePdfHook()
    const reValidateCi = useRecycleConfigurationItemBiznisMdulesHook()
    const { wrapperRef, scrollToMutationFeedback } = useScroll()
    const focusTriggerRef = useRef<() => void>(null)

    const [showInvalidate, setShowInvalidate] = useState<boolean>(false)
    const [showGeneratePdf, setGeneratePdf] = useState<boolean>(false)
    const [showApprove, setShowApprove] = useState<boolean>(false)
    const [showRevalidate, setRevalidate] = useState<boolean>(false)
    const [showReturnToWorkout, setShowReturnToWorkout] = useState<boolean>(false)
    const [isPdfDisabled, setIsPdfDisabled] = useState<boolean | undefined>(true)
    const [isLoadingApi, setIsLoadingApi] = useState<boolean>(false)
    const [isLoadingApproval, setIsLoadingApproval] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [showWarning, setShowWarning] = useState<boolean>(false)
    const [localLoading, setLocalLoading] = useState<boolean>(false)
    const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined)
    const [bulkActionResult, setBulkActionResult] = useState<IBulkActionResult>()
    const { data: xWikiDocData, fetchStatus: wikiDocFetchStatus } = useGetDocument(
        ENTITY_KRIS,
        entityId,
        KRIT_HODNOTENIE,
        {},
        { query: { queryKey: getGetDocumentQueryKey(ENTITY_KRIS, entityId, KRIT_HODNOTENIE), retry: 2, enabled: token !== null && !!user?.uuid } },
    )
    const isLoggedIn = !!user

    const entityListData = entityData ? [entityData] : []
    const canGetKrisData =
        entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_stav_kris] !== 'c_stav_kris.1' &&
        entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_stav_kris] !== undefined

    const {
        data: dataNeighbours,
        isLoading: isLoadingNeighbours,
        isError: isErrorNeighbours,
    } = useReadNeighboursConfigurationItems(entityId, { nodeType: 'Dokument', relationshipType: 'Dokument_sa_tyka_KRIS' })
    const { data: dataKris, isLoading: isLoadingGetKris } = useGetKris(entityData?.uuid ?? '', {
        query: {
            queryKey: getGetKrisQueryKey(entityData?.uuid ?? ''),
            enabled: canGetKrisData && isLoggedIn,
        },
    })
    const isLoadingKris = isLoggedIn && isLoadingGetKris && canGetKrisData
    const { data: isOwnerByGid } = useIsOwnerByGid(
        {
            gids: [entityData?.metaAttributes?.owner ?? ''],
            login: user?.login,
        },
        {
            query: {
                queryKey: getIsOwnerByGidQueryKey({
                    gids: [entityData?.metaAttributes?.owner ?? ''],
                    login: user?.login,
                }),
                enabled: entityData?.metaAttributes?.owner !== undefined && token !== null && !!user?.uuid,
            },
        },
    )
    const { data: dataPoRole, isLoading: isLoadingDataPoRole } = useGetRoleParticipant(entityData?.metaAttributes?.owner ?? '')

    const handleBulkAction = (actionResult: IBulkActionResult) => {
        setBulkActionResult(actionResult)
        refetchCi()
    }

    const handleCloseBulkModal = (actionResult: IBulkActionResult, closeFunction: (value: React.SetStateAction<boolean>) => void) => {
        closeFunction(false)
        handleBulkAction(actionResult)
    }

    const handleSave = (formData: FieldValues, close: (val: boolean) => void) => {
        setIsLoadingApi(true)
        setShowApprove(false)
        updateProtokol(`KRIS/${entityId}/protocol?shared=true&`, formData)
            .then(() => {
                close(true)
                setShowApprove(false)
                setSuccessMessage(t('feedback.approveSaved'))
            })
            .catch(() => {
                close(false)
            })
            .finally(() => {
                setIsLoadingApi(false)
            })
    }

    useEffect(() => {
        scrollToMutationFeedback()
    }, [bulkActionResult, scrollToMutationFeedback])

    const canApproveAsOwner = () => {
        const typeOfPO = dataPoRole?.configurationItemUi?.attributes?.[ATTRIBUTE_NAME.EA_Profil_PO_typ_osoby]
        const stateOfKris = entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_stav_kris]
        const allowedStateOfKris = stateOfKris === 'c_stav_kris.1' || stateOfKris === 'c_stav_kris.4'

        if (allowedStateOfKris && typeOfPO === 'c_typ_osoby.c1' && isOwnerByGid?.isOwner?.[0]?.owner) {
            return true
        }
        return false
    }

    const canSignDoc = (type: string) => {
        const canSign =
            dataNeighbours?.fromCiSet?.some((item) =>
                (item?.attributes as unknown as ConfigurationItemUiAttributes[])?.some(
                    (element) => element.name === ATTRIBUTE_NAME.Gen_Profil_kod_metais && (element?.value as string)?.startsWith(type),
                ),
            ) ?? false

        return canSign
    }

    const disableGeneratePdf = useCallback(
        async (newCi: ConfigurationItemUi | undefined) => {
            if (!dataPoRole) return true

            const res = await isInPoByGid({
                identityUuid: user?.uuid,
                cmdbId: dataPoRole?.configurationItemUi?.uuid,
            })
            let att = false
            if (newCi) {
                att = newCi.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_Zavazok_ciele_principy_stav] === true
            } else {
                att = entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_Zavazok_ciele_principy_stav] === true
            }
            return res === false || att === false
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataPoRole, updateButton],
    )

    const canShowGeneratePdf = () => {
        return !isInvalidated
    }

    const canShowSignDoc = () => {
        return !isInvalidated && canSignDoc('KRIS export ')
    }

    const canShowSignDocFuture = () => {
        return !isInvalidated && canSignDoc('KRIS future state export ')
    }

    const canApprove = () => {
        return !isInvalidated && (entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_stav_kris] === 'c_stav_kris.3' || canApproveAsOwner())
    }

    const canReValidate = () => {
        return isInvalidated && (isOwnerByGid?.isOwner?.[0]?.owner || user?.roles?.includes('R_ADMIN'))
    }

    const canInvalidate = () => {
        return !isInvalidated && (isOwnerByGid?.isOwner?.[0]?.owner || user?.roles?.includes('R_ADMIN'))
    }

    const canReturnToWorkout = () => {
        const manualApprove = dataKris?.manualApproved

        return !isInvalidated && !manualApprove && entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_stav_kris] === 'c_stav_kris.3'
    }

    const canSendToApprove = () => {
        const stateOfKris = entityData?.attributes?.[ATTRIBUTE_NAME.Profil_KRIS_stav_kris]
        const allowedStateOfKris = stateOfKris === 'c_stav_kris.1' || stateOfKris === 'c_stav_kris.4'

        return !isInvalidated && !canApproveAsOwner() && allowedStateOfKris
    }

    const approveAndSubmitProtocolWithoutChangingOwner = async (
        formData: IApproveFormData,
        protocolUuid: string,
        relationUuid: string,
        close: (val: boolean) => void,
    ) => {
        try {
            const roleRes = await getFindRole({ name: 'KRIS_SCHVAL' })
            const role = roleRes as Role

            const groupRes = await createGroup(role?.uuid ?? '', dataPoRole?.configurationItemUi?.uuid ?? '')
            const storeGraphRequest = {
                ...getBaseGraphApprove(
                    groupRes.gid ?? '',
                    entityData?.metaAttributes?.owner ?? '',
                    formData,
                    protocolUuid,
                    relationUuid,
                    entityData?.metaAttributes,
                ),
                changeOwnerSet: {},
            }

            await approveKris(storeGraphRequest)
            setSuccessMessage(t('ciType.approveSuccess'))
            await refetchCi()
            close(true)
        } catch (error) {
            setIsError(true)
            close(false)
        } finally {
            queryClient.invalidateQueries({ queryKey: getReadNeighboursConfigurationItemsQueryKey(entityId, { nodeType: 'KRIS_Protokol' }) })
            setIsLoadingApi(false)
        }
    }

    const approveAndSubmitProtocolWithChangingOwner = async (
        formData: IApproveFormData,
        protocolUuid: string,
        relationUuid: string,
        close: (val: boolean) => void,
    ) => {
        setIsLoadingApi(true)
        try {
            const akvsRes = await getCiListFIltered(FILTER_PARAM)
            const akvs = akvsRes?.configurationItemSet?.[0]

            const roleRes = await getFindRole({ name: 'KRIS_SCHVAL' })
            const role = roleRes as Role

            const groupRes = await createGroup(role?.uuid ?? '', akvs?.uuid ?? '')
            const historyRes = await getHistoryVersions(entityId, { page: 1, perPage: 100 })
            const originalOwner = getOriginalOwner(historyRes, entityData)

            const storeGraphRequest = {
                ...getBaseGraphApprove(groupRes.gid ?? '', originalOwner, formData, protocolUuid, relationUuid, entityData),
                changeOwnerSet: {
                    configurationItemSet: [
                        {
                            type: entityData?.type,
                            uuid: entityData?.uuid,
                            attributes: createNewKrisAttributes(entityData) as unknown as ConfigurationItemUiAttributes,
                        },
                    ],
                    relationshipSet: [],
                    changeOwnerData: {
                        newOwner: originalOwner,
                        changeReason: 'Schválenie KRIS',
                        changeDescription: 'Schválenie KRIS',
                        changeType: 'changeCmdbItem',
                    },
                },
            } as GraphRequestUi

            await approveKris(storeGraphRequest)
            setSuccessMessage(t('feedback.approveSuccess'))
            await refetchCi()
            close(true)
        } catch (error) {
            setIsError(true)
            close(false)
        } finally {
            queryClient.invalidateQueries({ queryKey: getReadNeighboursConfigurationItemsQueryKey(entityId, { nodeType: 'KRIS_Protokol' }) })
            setIsLoadingApi(false)
        }
    }

    const handleApprove = (formData: FieldValues, close: (val: boolean) => void) => {
        setShowApprove(true)
        setIsLoadingApproval(true)
        generateUuid()
            .then((protocolUuid) => {
                generateUuid()
                    .then((relationUuid) => {
                        if (canApproveAsOwner()) {
                            approveAndSubmitProtocolWithoutChangingOwner(formData, protocolUuid, relationUuid, close)
                        } else {
                            approveAndSubmitProtocolWithChangingOwner(formData, protocolUuid, relationUuid, close)
                        }
                    })
                    .finally(() => setIsLoadingApproval(false))
            })
            .catch(() => setIsLoadingApproval(false))
    }

    const handleGeneratePdf = async (formData: IGeneratePdfFormData) => {
        setIsLoadingApi(true)
        setGeneratePdf(false)
        setShowWarning(true)

        const projektsUuids = formData.project?.map((item) => item.uuid) as string[]
        const isvsUuids = formData.isvs?.map((item) => item.uuid) as string[]
        try {
            const dataFile = (await createPdf(entityId, {
                allIsvs: !isvsUuids,
                allProjects: !projektsUuids,
                isvsIds: isvsUuids,
                projectIds: projektsUuids,
            })) as unknown as Blob

            downloadBlobAsFile(new Blob([dataFile]), `KRIS_buduci_stav_${entityData?.attributes?.[ATTRIBUTE_NAME.Gen_Profil_kod_metais]}.pdf`, false)
        } catch (error) {
            setIsError(true)
        } finally {
            setIsLoadingApi(false)
        }
    }

    const handleRevvalidate = async () => {
        setRevalidate(false)
        setIsLoadingApi(true)
        try {
            await reValidateCi(entityId)
            setSuccessMessage(t('feedback.sendToRevalidate'))
            await refetchCi()
        } catch (error) {
            setIsError(true)
        } finally {
            setIsLoadingApi(false)
        }
    }

    const handleReturnToWorkout = async (formData: IReturnToWorkoutFormData) => {
        setIsLoadingApi(true)
        setShowReturnToWorkout(false)
        try {
            const protocolUuid = await generateUuid()
            const relationUuid = await generateUuid()
            const akvsRes = await getCiListFIltered(FILTER_PARAM)
            const akvs = akvsRes?.configurationItemSet?.[0]

            const roleRes = await getFindRole({ name: 'KRIS_SCHVAL' })
            const role = roleRes as Role

            const groupRes = await createGroup(role?.uuid ?? '', akvs?.uuid ?? '')
            const historyRes = await getHistoryVersions(entityId, { page: 1, perPage: 100 })
            const originalOwner = getOriginalOwner(historyRes, entityData)
            const storeGraphRequest = getBaseGraphReturnToWorkout(groupRes.gid ?? '', originalOwner, formData, protocolUuid, relationUuid, entityData)

            await approveKris(storeGraphRequest)
            setSuccessMessage(t('feedback.returnToWorkSuccess'))
            await refetchCi()
        } catch (error) {
            setIsError(true)
        } finally {
            setIsLoadingApi(false)
        }
    }

    const handleSendToApproving = async () => {
        setIsLoadingApi(true)
        try {
            const akvsRes = await getCiListFIltered(FILTER_PARAM)
            const akvs = akvsRes?.configurationItemSet?.[0]

            const roleRes = await getFindRole({ name: 'KRIS_SCHVAL' })
            const role = roleRes as Role

            const groupRes = await createGroup(role?.uuid ?? '', akvs?.uuid ?? '')
            const storeGraphRequest = getBaseGraphSendToApproving(groupRes.gid ?? '', entityData)

            const request = await approveKris(storeGraphRequest)
            await getRequestStatus(request.requestId ?? '')

            setSuccessMessage(t('feedback.sendToApprover'))
            await refetchCi()
        } catch (error) {
            setIsError(true)
        } finally {
            setIsLoadingApi(false)
            focusTriggerRef.current?.()
        }
    }

    const handleSignDoc = async () => {
        return
    }

    const handleSignDocFuture = async () => {
        return
    }

    useEffect(() => {
        const fetchDisabledStatus = async () => {
            const newCi = (await refetchCi()).data

            if (user) {
                const status = await disableGeneratePdf(newCi)
                setIsPdfDisabled(status)
            }
        }
        fetchDisabledStatus()
    }, [disableGeneratePdf, queryClient, refetchCi, updateButton, user])

    const isAnyModalOpened = () => [showGeneratePdf, showReturnToWorkout, showRevalidate, showApprove, showInvalidate].includes(true)

    const xWikiDoc = useGetDocumentHook()

    const createAndOpenXWikiDoc = () => {
        setLocalLoading(true)
        xWikiDoc(ENTITY_KRIS, entityData?.uuid ?? '', KRIT_HODNOTENIE, { createIfNotExists: true })
            .then((doc) => doc.xwikiUrl && window.open(doc.xwikiUrl, '_blank'))
            .catch(() => setBulkActionResult({ isSuccess: false, isError: true }))
            .finally(() => {
                queryClient.invalidateQueries({ queryKey: getGetDocumentQueryKey(ENTITY_KRIS, entityData?.uuid ?? '', KRIT_HODNOTENIE) })
                setLocalLoading(false)
            })
    }

    const isLoading = [
        isBulkLoading,
        isLoadingApi,
        isLoadingNeighbours,
        isLoadingDataPoRole,
        isLoadingKris,
        localLoading,
        wikiDocFetchStatus != 'idle',
    ].some((item) => item)

    return (
        <>
            <div ref={wrapperRef}>
                <MutationFeedback
                    success={bulkActionResult?.isSuccess}
                    successMessage={bulkActionResult?.successMessage}
                    error={bulkActionResult?.isError || isError}
                    onMessageClose={() => setBulkActionResult(undefined)}
                />
            </div>
            <MutationFeedback
                success={successMessage !== undefined}
                successMessage={successMessage}
                error={isError || isErrorNeighbours}
                onMessageClose={() => setSuccessMessage(undefined)}
            />
            <MutationFeedback
                success={showWarning}
                successMessage={t('modalKris.generatePdf.docGenStart')}
                error={false}
                onMessageClose={() => setShowWarning(false)}
            />
            <div className={styles.headerDiv}>
                {isLoading && <LoadingIndicator fullscreen />}
                <TextHeading size="XL">
                    <Del isInvalid={isInvalidated}>{entityItemName}</Del>
                </TextHeading>
                <ButtonGroupRow>
                    <Can I={Actions.EDIT} a={`ci.${entityId}`}>
                        {editButton}
                    </Can>
                    {isLoggedIn && (
                        <ButtonPopup
                            buttonClassName={styles.noWrap}
                            buttonLabel={t('ciType.moreButton')}
                            popupHorizontalPosition="right"
                            manuallyHandledFocusAfterClose
                            onClose={() => {
                                if (!isAnyModalOpened()) {
                                    focusTriggerRef.current?.()
                                }
                            }}
                            popupContent={(_, focusTriggerEl) => {
                                focusTriggerRef.current = focusTriggerEl

                                return (
                                    <div className={styles.buttonLinksDiv}>
                                        {canShowGeneratePdf() && (
                                            <Tooltip
                                                disabled={!isPdfDisabled}
                                                key={'pdfGenerateFuture'}
                                                descriptionElement={t('tooltip.NO_FUTURE_PDF_GOALS')}
                                                position={'top center'}
                                                tooltipContent={() => (
                                                    <ButtonLink
                                                        disabled={isPdfDisabled}
                                                        onClick={() => {
                                                            setGeneratePdf(true)
                                                        }}
                                                        label={t('ciType.pdfGenerateFuture')}
                                                        aria={{ 'aria-haspopup': 'dialog' }}
                                                    />
                                                )}
                                            />
                                        )}
                                        {canInvalidate() && (
                                            <Tooltip
                                                key={'invalidateItem'}
                                                descriptionElement={errorMessage}
                                                position={'top center'}
                                                tooltipContent={(open) => (
                                                    <ButtonLink
                                                        disabled={isInvalidated}
                                                        onClick={() =>
                                                            handleInvalidate(
                                                                entityListData,
                                                                () => {
                                                                    setShowInvalidate(true)
                                                                },
                                                                open,
                                                            )
                                                        }
                                                        label={t('ciType.invalidateItem')}
                                                        aria={{ 'aria-haspopup': 'dialog' }}
                                                    />
                                                )}
                                            />
                                        )}
                                        {canReValidate() && (
                                            <Tooltip
                                                key={'reInvalidateCi'}
                                                descriptionElement={errorMessage}
                                                position={'top center'}
                                                tooltipContent={() => (
                                                    <ButtonLink
                                                        disabled={!isInvalidated}
                                                        onClick={() => {
                                                            setRevalidate(true)
                                                        }}
                                                        label={t('ciType.reInvalidateCi')}
                                                        aria={{ 'aria-haspopup': 'dialog' }}
                                                    />
                                                )}
                                            />
                                        )}
                                        <Can I={Actions.APPROVE_KRIS} a={`ci.${entityId}`}>
                                            {canApprove() && (
                                                <Tooltip
                                                    key={'approve'}
                                                    descriptionElement={errorMessage}
                                                    position={'top center'}
                                                    tooltipContent={() => (
                                                        <ButtonLink
                                                            disabled={!isOwnerByGid?.isOwner?.[0]?.owner}
                                                            onClick={() => {
                                                                setShowApprove(true)
                                                            }}
                                                            label={t('ciType.approve')}
                                                            aria={{ 'aria-haspopup': 'dialog' }}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Can>

                                        {canReturnToWorkout() && (
                                            <Tooltip
                                                key={'return_to_workout'}
                                                descriptionElement={errorMessage}
                                                position={'top center'}
                                                tooltipContent={() => (
                                                    <ButtonLink
                                                        disabled={!isEvaluation}
                                                        onClick={() => {
                                                            setShowReturnToWorkout(true)
                                                        }}
                                                        label={t('ciType.return_to_workout')}
                                                        aria={{ 'aria-haspopup': 'dialog' }}
                                                    />
                                                )}
                                            />
                                        )}
                                        <Can I={Actions.KRIS_SEND_APPROVING} a={`ci.${entityId}`}>
                                            {canSendToApprove() && (
                                                <Tooltip
                                                    key={'sentToApprove'}
                                                    descriptionElement={errorMessage}
                                                    position={'top center'}
                                                    tooltipContent={() => (
                                                        <ButtonLink
                                                            disabled={!isOwnerByGid?.isOwner?.[0]?.owner}
                                                            onClick={handleSendToApproving}
                                                            label={t('ciType.authorizeSendToApprove')}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Can>
                                        <Can I={Actions.KRIS_SUBSCRIBE} a={`ci.${entityId}`}>
                                            {canShowSignDoc() && (
                                                <Tooltip
                                                    key={'signDocument'}
                                                    descriptionElement={errorMessage}
                                                    position={'top center'}
                                                    tooltipContent={() => (
                                                        <ButtonLink
                                                            disabled={!isOwnerByGid?.isOwner?.[0]?.owner}
                                                            onClick={() => {
                                                                handleSignDoc()
                                                            }}
                                                            label={t('ciType.signDocument')}
                                                            aria={{ 'aria-haspopup': 'dialog' }}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Can>
                                        <Can I={Actions.KRIS_SUBSCRIBE} a={`ci.${entityId}`}>
                                            {canShowSignDocFuture() && (
                                                <Tooltip
                                                    key={'signDocumentFuture'}
                                                    descriptionElement={errorMessage}
                                                    position={'top center'}
                                                    tooltipContent={() => (
                                                        <ButtonLink
                                                            disabled={!isOwnerByGid?.isOwner?.[0]?.owner}
                                                            onClick={() => {
                                                                handleSignDocFuture()
                                                            }}
                                                            label={t('ciType.signDocumentFuture')}
                                                            aria={{ 'aria-haspopup': 'dialog' }}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Can>
                                        {!xWikiDocData && (
                                            <Can I={Actions.APPROVE_KRIS} a={`ci.${entityId}`}>
                                                <ButtonLink
                                                    key={'buttonOpen'}
                                                    label={t('ciType.generateOpinion')}
                                                    onClick={() => createAndOpenXWikiDoc()}
                                                />
                                            </Can>
                                        )}
                                    </div>
                                )
                            }}
                        />
                    )}
                </ButtonGroupRow>
                {showGeneratePdf && dataPoRole?.owner && (
                    <GeneratePdfModal
                        open={showGeneratePdf}
                        orgId={dataPoRole?.owner}
                        onClose={() => {
                            setGeneratePdf(false)
                            focusTriggerRef.current?.()
                        }}
                        onSend={(formData) => {
                            handleGeneratePdf(formData)
                        }}
                    />
                )}
                <ReturnToWorkoutModal
                    open={showReturnToWorkout}
                    onClose={() => {
                        setShowReturnToWorkout(false)
                        focusTriggerRef.current?.()
                    }}
                    onSend={(formData) => handleReturnToWorkout(formData)}
                />
                <ReValidateModal
                    open={showRevalidate}
                    onClose={() => {
                        setRevalidate(false)
                        focusTriggerRef.current?.()
                    }}
                    onSend={handleRevvalidate}
                />
                <ApproveModal
                    user={user}
                    uuid={entityId}
                    open={showApprove}
                    onClose={() => {
                        setShowApprove(false)
                        focusTriggerRef.current?.()
                    }}
                    isLoadingApproval={isLoadingApproval}
                    onSave={(formData: FieldValues, close: (val: boolean) => void) => handleSave(formData, close)}
                    onApproveKris={(formData, close: (val: boolean) => void) => handleApprove(formData, close)}
                />
                <InvalidateBulkModal
                    items={entityListData}
                    open={showInvalidate}
                    onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, setShowInvalidate)}
                    onClose={() => {
                        setShowInvalidate(false)
                        focusTriggerRef.current?.()
                    }}
                    isRelation={isRelation}
                />
            </div>
        </>
    )
}
